/**
 * Approval gate (Phase 65 / BLU-594 PR3b) — the core operator checkpoint.
 *
 * ZERO Shopify/storage writes have happened yet. Renders one card per
 * approvable product (design code, variant count, the files that will be
 * written, lookup + metafield counts, conflict warnings) with a per-card
 * checkbox (all approvable selected by default). Blocked products render as
 * excluded red-muted cards with their reason. Approving sends only the ticked
 * ids to `approveProductSyncJob`, which starts the real writes. A plan with
 * nothing to review never reaches this gate (BLU-1522 S5): it starts on its own.
 *
 * At the 500-product job cap, rendering every card up front is a lot of DOM for
 * a list an operator scans rather than reads, so the first `VISIBLE_CARD_LIMIT`
 * render with an explicit "showing first N of M" note plus a one-tap reveal —
 * never a silent truncation, because a card you cannot see is a product you
 * cannot untick.
 *
 * DECLARED PLANS (Phase 88, store SKU set-up). A plan whose `identity` is
 * `declared` renders its own review: it writes database rows only, so it says
 * "Nothing is written to your store.", warns (never blocks) how many SKUs a
 * platen size rule would hold for want of a per-size file, lists each
 * product's variants to set up and groups the skipped ones by reason in plain
 * words. No lookup-row or metafield counts. It re-reads the store SKU switch
 * with `staleTime: 0` and its own loading and error states, so a stale cached
 * "on" can never enable Approve; execution re-reads it again as the backstop.
 * A derived plan renders exactly as before and makes no switch query.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, ArrowRight, FileImage, Loader2, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SectionError } from '@/components/shared/SectionError';
import { WarningNotice } from '@/components/shared/WarningNotice';
import type { PlanBlocker, PlanProduct, SyncPlan } from '../../lib/apply-types';
import { blockerMessageKey, groupBlockersByProduct } from '../../lib/blocker-copy';
import { declaredReasonKey } from '../../lib/store-skus';

interface JobApprovalGateProps {
  jobId: string;
  plan: SyncPlan;
  admin: boolean;
  onRequestCancel: () => void;
}

/** Cards rendered before the reveal. Keeps a 500-product plan off the critical path without hiding anything silently. */
const VISIBLE_CARD_LIMIT = 100;

export function JobApprovalGate(props: JobApprovalGateProps) {
  return props.plan.identity === 'declared' ? <DeclaredApprovalGate {...props} /> : <DerivedApprovalGate {...props} />;
}

/** The ticked products, the approve mutation and the bounded card reveal: shared by both reviews. */
function useApprovalSelection(jobId: string, plan: SyncPlan) {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();
  const [approvedIds, setApprovedIds] = useState<Set<string>>(() => new Set(plan.products.map((p) => p.productId)));
  const [showAll, setShowAll] = useState(false);

  const approve = trpc.productSync.approveProductSyncJob.useMutation({
    onSuccess: () => {
      toast.success(t('job.approve.success', { count: approvedIds.size }));
      void utils.productSync.getProductSyncJob.invalidate({ jobId });
      // Approval REWRITES the stored plan down to the approved products, so the
      // fetched-once plan in cache is now stale (it is the run's denominator).
      void utils.productSync.getProductSyncJobPlan.invalidate({ jobId });
    },
    onError: (e) => toast.error(t('job.approve.error', { message: e.message })),
  });

  const toggle = (id: string) =>
    setApprovedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const totalProducts = plan.products.length;
  const selectedCount = approvedIds.size;
  const visibleProducts = useMemo(
    () => (showAll ? plan.products : plan.products.slice(0, VISIBLE_CARD_LIMIT)),
    [plan.products, showAll],
  );
  // 86-23 review: one product can carry several blockers; it renders as ONE
  // card listing all of them, so the cap and the counts are per product.
  const blockerGroups = useMemo(() => groupBlockersByProduct(plan.blockers), [plan.blockers]);
  const visibleBlockers = useMemo(
    () => (showAll ? blockerGroups : blockerGroups.slice(0, VISIBLE_CARD_LIMIT)),
    [blockerGroups, showAll],
  );
  const hiddenCards = totalProducts - visibleProducts.length + (blockerGroups.length - visibleBlockers.length);

  return {
    approvedIds,
    setApprovedIds,
    toggle,
    showAll,
    setShowAll,
    approve,
    totalProducts,
    selectedCount,
    visibleProducts,
    blockerGroups,
    visibleBlockers,
    hiddenCards,
  };
}


type ApprovalSelection = ReturnType<typeof useApprovalSelection>;

/** "N of M selected" with Select all / Deselect all, for a plan with more than one product. */
function SelectionControls({ plan, selection }: { plan: SyncPlan; selection: ApprovalSelection }) {
  const { t } = useTranslation('productSync');
  const { selectedCount, totalProducts, setApprovedIds } = selection;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-xs tabular-nums text-muted-foreground" aria-live="polite">
        {t('job.approve.selectedCount', { selected: selectedCount, total: totalProducts })}
      </p>
      <div className="flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={selectedCount === totalProducts}
          onClick={() => setApprovedIds(new Set(plan.products.map((p) => p.productId)))}
        >
          {t('job.approve.selectAll')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={selectedCount === 0}
          onClick={() => setApprovedIds(new Set())}
        >
          {t('job.approve.deselectAll')}
        </Button>
      </div>
    </div>
  );
}

/** The "showing the first N" note and its one-tap reveal. */
function RevealAll({ selection }: { selection: ApprovalSelection }) {
  const { t } = useTranslation('productSync');
  const { hiddenCards, visibleProducts, visibleBlockers, blockerGroups, totalProducts, setShowAll } = selection;
  if (hiddenCards <= 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed px-4 py-3">
      <div className="space-y-0.5">
        <p className="text-xs text-muted-foreground">
          {t('job.approve.showingFirst', { shown: visibleProducts.length, total: totalProducts })}
        </p>
        {blockerGroups.length > visibleBlockers.length && (
          <p className="text-xs text-muted-foreground">
            {t('job.approve.blockersShowingFirst', { shown: visibleBlockers.length, total: blockerGroups.length })}
          </p>
        )}
      </div>
      <Button variant="outline" size="sm" className="h-8 gap-1.5 px-3 text-xs" onClick={() => setShowAll(true)}>
        {t('job.approve.showAll')}
      </Button>
    </div>
  );
}

function DerivedApprovalGate({ jobId, plan, admin, onRequestCancel }: JobApprovalGateProps) {
  const { t } = useTranslation('productSync');
  const selection = useApprovalSelection(jobId, plan);
  const { approvedIds, toggle, approve, totalProducts, selectedCount, visibleProducts, visibleBlockers } = selection;
  // 86-23 review: a store SKU org's plan runs only once every blocker is
  // fixed, so they are all fixed before anything is written to the store.
  const mustFixFirst = plan.storeSkuByo === true && plan.blockers.length > 0;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('job.approve.heading')}
        </p>
        <p className="mt-1 text-xs text-amber-800">{t('job.approve.body')}</p>
      </div>

      {mustFixFirst && (
        <p role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50/60 px-4 py-3 text-xs text-red-800" data-testid="fix-blockers-first">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" aria-hidden="true" />
          {t('job.approve.fixBlockersFirst')}
        </p>
      )}

      {admin && totalProducts > 1 && <SelectionControls plan={plan} selection={selection} />}

      <div className="grid gap-3 lg:grid-cols-2">
        {visibleProducts.map((product) => (
          <ApprovalCard
            key={product.productId}
            product={product}
            checked={approvedIds.has(product.productId)}
            admin={admin}
            onToggle={() => toggle(product.productId)}
          />
        ))}
        {visibleBlockers.map((group) => (
          <BlockerCard key={group[0]!.productId} blockers={group} mustFix={mustFixFirst} />
        ))}
      </div>

      <RevealAll selection={selection} />

      {admin ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
          <Button variant="ghost" size="sm" onClick={onRequestCancel} className="h-9 gap-1.5 px-3 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700">
            {t('job.cancel.button')}
          </Button>
          <Button
            size="sm"
            onClick={() => approve.mutate({ jobId, approvedProductIds: [...approvedIds] })}
            disabled={selectedCount === 0 || approve.isPending || mustFixFirst}
            className="h-9 gap-1.5 bg-threadheads-purple px-4 text-xs text-white hover:bg-threadheads-purple/90"
          >
            {approve.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
            {t('job.approve.cta', { count: selectedCount })}
          </Button>
        </div>
      ) : (
        <p className="border-t pt-4 text-sm text-muted-foreground/70">{t('job.approve.readOnly')}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Declared (store SKU set-up) review.
// ---------------------------------------------------------------------------

type SwitchState = 'checking' | 'error' | 'off' | 'on';

/** The review-time switch read as one state: only data fetched since mount counts. */
function switchStateOf(query: {
  isError: boolean;
  isPending: boolean;
  isFetchedAfterMount: boolean;
  data?: { mode: string };
}): SwitchState {
  if (query.isError) return 'error';
  if (query.isPending || !query.isFetchedAfterMount || !query.data) return 'checking';
  return query.data.mode === 'on' ? 'on' : 'off';
}

function DeclaredApprovalGate({ jobId, plan, admin, onRequestCancel }: JobApprovalGateProps) {
  const { t } = useTranslation('productSync');
  const selection = useApprovalSelection(jobId, plan);
  const { approvedIds, toggle, approve, totalProducts, selectedCount, visibleProducts, visibleBlockers } = selection;

  // Re-read the switch at review time: `staleTime: 0` and only data fetched
  // since this review mounted counts, so a cached "on" from another screen can
  // never enable Approve. Only an admin can approve, so only an admin reads it.
  const switchQuery = trpc.productSync.getDeclaredSkuMode.useQuery(undefined, {
    enabled: admin,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const switchState = switchStateOf(switchQuery);

  // The held count follows the ticks: the sum of each ticked product's own
  // count, so unticking a product drops its SKUs from the warning. A plan
  // written before products carried their own count falls back to its total.
  const perProductHeldKnown = plan.products.every((p) => typeof p.ruledWithoutFile === 'number');
  const ruledWithoutFile = perProductHeldKnown
    ? plan.products.reduce((sum, p) => (approvedIds.has(p.productId) ? sum + (p.ruledWithoutFile ?? 0) : sum), 0)
    : (plan.totals.ruledWithoutFile ?? 0);
  // Every product blocked: there is nothing to approve, so there is no Approve.
  const showApprove = totalProducts > 0 && (switchState === 'on' || switchState === 'checking');

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('job.approve.heading')}
        </p>
        <p className="mt-1 text-xs text-amber-800">{t('job.approve.declaredNoStoreWrites')}</p>
      </div>

      {/* A warning, never a block (S2). */}
      {ruledWithoutFile > 0 && (
        <WarningNotice data-testid="declared-held-count">
          {t('job.approve.declaredRuledWithoutFile', { count: ruledWithoutFile })}
        </WarningNotice>
      )}

      {admin && switchState === 'error' && (
        <SectionError
          variant="compact"
          title={t('job.approve.declaredSwitchError')}
          retryLabel={t('states.retry')}
          retrying={switchQuery.isFetching}
          onRetry={() => void switchQuery.refetch()}
        />
      )}
      {admin && switchState === 'off' && (
        <WarningNotice role="status">{t('job.approve.declaredSwitchChanged')}</WarningNotice>
      )}

      {admin && totalProducts > 1 && <SelectionControls plan={plan} selection={selection} />}

      <div className="grid gap-3 lg:grid-cols-2">
        {visibleProducts.map((product) => (
          <DeclaredApprovalCard
            key={product.productId}
            product={product}
            checked={approvedIds.has(product.productId)}
            admin={admin}
            onToggle={() => toggle(product.productId)}
          />
        ))}
        {visibleBlockers.map((group) =>
          group[0]!.skippedVariants ? (
            <DeclaredBlockerCard key={group[0]!.productId} blocker={group[0]!} />
          ) : (
            <BlockerCard key={group[0]!.productId} blockers={group} mustFix={false} />
          ),
        )}
      </div>

      <RevealAll selection={selection} />

      {admin ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
          <Button variant="ghost" size="sm" onClick={onRequestCancel} className="h-9 gap-1.5 px-3 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700">
            {t('job.cancel.button')}
          </Button>
          {showApprove && (
            <Button
              size="sm"
              onClick={() => approve.mutate({ jobId, approvedProductIds: [...approvedIds] })}
              disabled={switchState !== 'on' || selectedCount === 0 || approve.isPending}
              className="h-9 gap-1.5 bg-threadheads-purple px-4 text-xs text-white hover:bg-threadheads-purple/90"
            >
              {approve.isPending || switchState === 'checking' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : null}
              {t('job.approve.cta', { count: selectedCount })}
            </Button>
          )}
        </div>
      ) : (
        <p className="border-t pt-4 text-sm text-muted-foreground/70">{t('job.approve.readOnly')}</p>
      )}
    </div>
  );
}

/** Skipped variants grouped by reason, in the plan's order of first appearance. */
function groupSkipped(skipped: NonNullable<PlanProduct['skippedVariants']>): Array<{ reason: string; count: number }> {
  const counts = new Map<string, number>();
  for (const variant of skipped) counts.set(variant.outcome, (counts.get(variant.outcome) ?? 0) + 1);
  return [...counts.entries()].map(([reason, count]) => ({ reason, count }));
}

function SkippedLines({ skipped }: { skipped: PlanProduct['skippedVariants'] }) {
  const { t } = useTranslation('productSync');
  if (!skipped || skipped.length === 0) return null;
  return (
    <ul className="mt-2 space-y-0.5">
      {groupSkipped(skipped).map(({ reason, count }) => {
        const key = declaredReasonKey(reason);
        return (
          <li key={reason} className="text-[11px] text-muted-foreground">
            {t('job.approve.declaredSkipped', { count, reason: key ? t(key) : t('status.needsAttention') })}
          </li>
        );
      })}
    </ul>
  );
}

function DeclaredApprovalCard({ product, checked, admin, onToggle }: { product: PlanProduct; checked: boolean; admin: boolean; onToggle: () => void }) {
  const { t } = useTranslation('productSync');
  return (
    <div className={cn('rounded-lg border bg-background p-3.5 transition-colors duration-200 motion-reduce:transition-none', checked ? 'border-threadheads-purple/30' : 'opacity-70')}>
      <div className="flex items-start gap-2.5">
        <Checkbox checked={checked} onCheckedChange={onToggle} disabled={!admin} aria-label={t('job.approve.declaredToggleAria', { title: product.title })} className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold" title={product.title}>{product.title}</p>
          <p className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
            {t('job.approve.declaredVariants', { count: product.variants.length })}
          </p>
          {product.variants.length > 0 && (
            <ul className="mt-2 flex max-h-32 flex-wrap gap-1 overflow-y-auto">
              {product.variants.map((variant) => (
                <li key={variant.variantId} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10.5px] text-foreground">
                  {variant.sku}
                </li>
              ))}
            </ul>
          )}
          <SkippedLines skipped={product.skippedVariants} />
        </div>
      </div>
    </div>
  );
}

function DeclaredBlockerCard({ blocker }: { blocker: PlanBlocker }) {
  const { t } = useTranslation('productSync');
  const key = declaredReasonKey(blocker.reason);
  return (
    <div className="rounded-lg border border-red-200 bg-red-50/60 p-3.5">
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          {blocker.title && <p className="truncate text-xs font-semibold text-red-800" title={blocker.title}>{blocker.title}</p>}
          <p className={cn('text-[11px] text-red-700', blocker.title && 'mt-1')}>
            {t('job.approve.excluded')}: {key ? t(key) : t(`job.blocker.${blocker.reason}`, { defaultValue: blocker.reason })}
          </p>
          <SkippedLines skipped={blocker.skippedVariants} />
        </div>
      </div>
    </div>
  );
}

function ApprovalCard({ product, checked, admin, onToggle }: { product: PlanProduct; checked: boolean; admin: boolean; onToggle: () => void }) {
  const { t } = useTranslation('productSync');
  // Split by action rather than labelling the whole list from conflicts[0]: one
  // product can mix overwrites with kept rows (skip / existing_custom_kept),
  // and telling an operator "will be overwritten" when a row is kept is the one
  // lie this gate cannot afford.
  const overwriteCount = product.conflicts.reduce((n, c) => (c.action === 'overwrite' ? n + 1 : n), 0);
  const keptCount = product.conflicts.length - overwriteCount;
  // Phase 86 slice B (R3-12): the variants whose light / dark / medium this
  // run changes in the store (distinct variants, not metafields).
  const storeUpdateCount = new Set((product.plannedMetafieldUpdates ?? []).map((u) => u.variantId)).size;

  return (
    <div className={cn('rounded-lg border bg-background p-3.5 transition-colors duration-200 motion-reduce:transition-none', checked ? 'border-threadheads-purple/30' : 'opacity-70')}>
      <div className="flex items-start gap-2.5">
        <Checkbox checked={checked} onCheckedChange={onToggle} disabled={!admin} aria-label={t('job.approve.toggleAria', { code: product.designCode })} className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            {product.title && <span className="truncate text-sm font-semibold" title={product.title}>{product.title}</span>}
            <span className="shrink-0 rounded bg-threadheads-purple/10 px-1.5 py-0.5 font-mono text-[11px] font-bold text-threadheads-purple">{product.designCode}</span>
            {product.hasBack && <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">{t('job.approve.frontBack')}</span>}
          </div>
          <p className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
            {t('row.variants', { count: product.variants.length })}
          </p>

          {/* Files */}
          <ul className="mt-2 space-y-1">
            {product.files.map((f, i) => (
              <li key={i} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <FileImage className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide">
                  {t(`platen.missingFiles.set.${f.set}`)} · {t(`platen.missingFiles.side.${f.side}`)}
                </span>
                <span className="truncate font-mono" title={f.storedFilename ?? f.sourceRef.type}>
                  {f.storedFilename ?? t(`job.source.${f.sourceRef.type}`)}
                </span>
              </li>
            ))}
          </ul>

          {/* Write counts */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span className="tabular-nums"><ArrowRight className="mr-0.5 inline h-3 w-3" aria-hidden="true" />{t('job.approve.lookupRows', { count: product.lookupRows })}</span>
            <span className="tabular-nums">{t('job.approve.metafieldWrites', { count: product.metafieldWrites })}</span>
          </div>
          {storeUpdateCount > 0 && (
            <p className="mt-1 text-[11px] tabular-nums text-muted-foreground">
              {t('job.approve.storeUpdates', { count: storeUpdateCount })}
            </p>
          )}

          {/* Conflicts */}
          {overwriteCount > 0 && (
            <p className="mt-2 flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
              {t('job.approve.conflictsOverwrite', { count: overwriteCount })}
            </p>
          )}
          {keptCount > 0 && (
            <p className="mt-2 flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
              {t('job.approve.conflictsSkip', { count: keptCount })}
            </p>
          )}

          <SupplyHeldBackLines heldBack={product.supply?.heldBack} />
        </div>
      </div>
    </div>
  );
}

/**
 * Phase 92: a supply product's variants this run leaves alone, as muted lines
 * under the card: the ones the client left out, then the ones whose print SKU
 * is already used, one line per location.
 */
function SupplyHeldBackLines({ heldBack }: { heldBack: NonNullable<PlanProduct['supply']>['heldBack'] | undefined }) {
  const { t } = useTranslation('productSync');
  if (!heldBack || heldBack.length === 0) return null;
  const notElected = heldBack.filter((v) => v.reason === 'not_elected').length;
  const inUseByLocation = new Map<string, number>();
  for (const v of heldBack) {
    if (v.reason !== 'print_sku_in_use') continue;
    const location = v.locationName ?? '';
    inUseByLocation.set(location, (inUseByLocation.get(location) ?? 0) + 1);
  }
  return (
    <ul className="mt-2 space-y-0.5" data-testid="supply-held-back">
      {notElected > 0 && (
        <li className="text-[11px] text-muted-foreground">{t('job.approve.supplyNotElected', { count: notElected })}</li>
      )}
      {[...inUseByLocation].map(([location, count]) => (
        <li key={location} className="text-[11px] text-muted-foreground">
          {t('job.approve.supplyHeldBack', { count, location })}
        </li>
      ))}
    </ul>
  );
}

/** One blocked product: every reason it carries, one line each (86-23 review). */
function BlockerCard({ blockers, mustFix }: { blockers: PlanBlocker[]; mustFix: boolean }) {
  const { t } = useTranslation('productSync');
  const title = blockers[0]?.title ?? '';
  const label = t(mustFix ? 'job.approve.needsFix' : 'job.approve.excluded');
  return (
    <div className="rounded-lg border border-red-200 bg-red-50/60 p-3.5">
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          {title && <p className="truncate text-xs font-semibold text-red-800" title={title}>{title}</p>}
          <ul className={cn('space-y-0.5', title && 'mt-1')}>
            {blockers.map((blocker, i) => (
              <li key={`${blocker.reason}-${blocker.sku ?? ''}-${i}`} className="text-[11px] text-red-700">
                {label}: {t(blockerMessageKey(blocker), { defaultValue: blocker.reason, location: blocker.locationName ?? '', sku: blocker.sku ?? '', type: blocker.productTypeCode ?? '', design: blocker.designCode ?? '' })}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
